// ---------------------------------------------------------------------------
// ใบกำกับภาษี / ใบเสร็จรับเงิน ของ Order Marketplace แยกตาม process_type (PresentGateway)
// ---------------------------------------------------------------------------
// - zenith_market (และ order เก่าที่ไม่มี process_type) = ใบเดิม report_tax_invoice ใน Report.js ห้ามเปลี่ยนผล
// - ระบบอื่น = ใบสไตล์ SaaS (ไม่มีที่อยู่จัดส่ง/ค่าส่ง) หัวผู้ขายมาจาก order.seller (gateway_companies)
//
// เพิ่ม PresentGateway ใหม่: เติมรายการใน INVOICE_BRANDS (logo + template) สีมาจาก PROCESS_TYPES
// ตัวที่ยังไม่มีในลิสต์จะใช้ template generic (seller + สีจาก PROCESS_TYPES ไม่มีโลโก้)
// ---------------------------------------------------------------------------
import pdfMake from "pdfmake/build/pdfmake";
import ThaiBahtText from "thai-baht-text";
import { toFixed, format_date } from "./CustomComponent.js";
import { tax_invoice_definition } from "./Report.js"; // import นี้ตั้งค่า pdfMake.vfs/fonts ให้ด้วย
import { DEFAULT_PROCESS_TYPE, processTypeStyle } from "./process-type.js";
import LOGO_LEVELUP_CRM from "../assets/images/crm/levelup-crm.png";
import LOGO_LUCID_CRM from "../assets/images/crm/lucid-crm.png";

// ปัดสตางค์ให้ลงท้าย 5 ตามสูตรตะกร้าของ Zenithlink-Market-Frontend (เหมือนในหน้า order-marketplace)
export const roundDecimalToNearest = (num) => {
  let baht = Math.floor(num);
  let satang = Math.round((num - baht) * 100);
  let rounded_satang = Math.round(satang / 5) * 5;
  if (rounded_satang >= 100) {
    baht += 1;
    rounded_satang = 0;
  }
  return baht + rounded_satang / 100;
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const isDiscountItem = (item) => String(item?.product_type || "").toLowerCase() === "discount";

const itemLineTotal = (item) =>
  Number(item?.total_price) || (Number(item?.price) || 0) * (Number(item?.quantity) || 0);

/**
 * ยอดสรุปของ order (ใช้ร่วมกันระหว่าง modal และ PDF ให้ตัวเลขตรงกัน)
 * - goods = Σ สินค้า (ไม่รวมบรรทัด discount) · discount = Σ |บรรทัด product_type discount|
 * - order market (zenith_market): VAT = Math.round(roundDecimalToNearest(goods - discount) × 0.07) แสดงอย่างเดียว (รวมในราคาแล้ว) เหมือนเดิม
 * - order PresentGateway (ราคาไม่รวม VAT): ยอดก่อนภาษี = Σสินค้า + ค่าดำเนินการ(fee) - ส่วนลด, VAT = total_price - ยอดก่อนภาษี
 *   vat_mode "added" เมื่อ |VAT - round2(ยอดก่อนภาษี×0.07)| <= 0.02 ไม่งั้น (บิลเก่า/ข้อมูลไม่ลงตัว)
 *   fallback vat_mode "included": VAT = total × 7/107 ปัดสตางค์ (แสดงอย่างเดียว)
 */
export const computeOrderSummary = (detail) => {
  const items = Array.isArray(detail?.order_items) ? detail.order_items : [];
  if (!isDigitalOrder(detail)) {
    // zenith_market: สูตรเดิมทุกอย่าง (รวมบรรทัด discount ติดลบของ cron เข้า items_total)
    const items_total = items.reduce((sum, item) => sum + itemLineTotal(item), 0);
    const subtotal = roundDecimalToNearest(items_total);
    const vat = Math.round(subtotal * 0.07);
    const fee = Number(detail?.fee) || 0;
    const amount = Number(detail?.transaction?.amount);
    return {
      goods: items_total,
      discount: 0,
      subtotal,
      vat,
      vat_mode: "included",
      fee,
      total: null,
      amount: Number.isFinite(amount) && detail?.transaction ? amount : subtotal + fee + vat,
    };
  }
  let goods = 0;
  let discount = 0;
  items.forEach((item) => {
    if (isDiscountItem(item)) discount += Math.abs(itemLineTotal(item));
    else goods += itemLineTotal(item);
  });
  const fee = Number(detail?.fee) || 0;
  const pre_tax = round2(goods + fee - discount);
  const tx_amount = Number(detail?.transaction?.amount);
  const has_tx_amount = Number.isFinite(tx_amount) && !!detail?.transaction;
  const raw_total = detail?.total_price;
  const total_price = Number(raw_total);
  const total =
    raw_total !== undefined && raw_total !== null && raw_total !== "" && Number.isFinite(total_price)
      ? total_price
      : has_tx_amount
      ? tx_amount
      : null;

  let vat = round2(pre_tax * 0.07);
  let vat_mode = "added";
  if (total !== null) {
    const vat_rest = round2(total - pre_tax);
    if (vat_rest >= 0 && Math.abs(vat_rest - vat) <= 0.02) vat = vat_rest;
    else {
      vat_mode = "included";
      vat = round2((total * 7) / 107);
    }
  }
  return {
    goods,
    discount,
    subtotal: roundDecimalToNearest(goods - discount),
    pre_tax,
    vat,
    vat_mode,
    fee,
    total,
    amount: has_tx_amount ? tx_amount : round2(pre_tax + vat),
  };
};

/** process_type ของ order (ข้อมูลเก่าที่ไม่มีค่า = zenith_market) */
export const getOrderProcessType = (detail) =>
  detail?.process_type || detail?.seller?.process_type || DEFAULT_PROCESS_TYPE;

/** order ของ PresentGateway ที่ไม่ใช่สินค้าจัดส่ง (ไม่มีที่อยู่จัดส่ง/ค่าส่ง) */
export const isDigitalOrder = (detail) => getOrderProcessType(detail) !== DEFAULT_PROCESS_TYPE;

// ---- brand registry --------------------------------------------------------
// process_type -> { brand, logo, template }   (สีใช้ PROCESS_TYPES[].color)
const INVOICE_BRANDS = {
  zenith_market: { template: "legacy" },
  levelup_crm: { brand: "LevelUpCRM", logo: LOGO_LEVELUP_CRM, template: "saas" },
  lucid_crm: { brand: "LucidCRM", logo: LOGO_LUCID_CRM, template: "saas" },
};

export const resolveInvoiceBrand = (process_type) => {
  const style = processTypeStyle(process_type);
  const entry = INVOICE_BRANDS[process_type] || { template: "saas" }; // generic
  return { brand: entry.brand || style.label, logo: entry.logo || null, color: style.color, template: entry.template };
};

// ---- helpers ---------------------------------------------------------------
const imageToDataUrl = async (url) => {
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null; // ไม่มีโลโก้ก็ออกใบได้
  }
};

const parseRemark = (remark) => {
  if (!remark) return {};
  if (typeof remark === "object") return remark;
  try {
    const parsed = JSON.parse(remark);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const CYCLE_LABEL = { monthly: "รายเดือน", quarterly: "รายไตรมาส", yearly: "รายปี", annual: "รายปี" };
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, yearly: 12, annual: 12 };
const pick = (obj, keys) => keys.map((k) => obj[k]).find((v) => v !== undefined && v !== null && v !== "");
const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : format_date(d, "dd/mm/yyyy");
};

/** บรรทัดรายละเอียดใต้ชื่อรายการ: แพ็กเกจ · รอบบริการ · ช่วงเวลา (อ่านจาก item.remark JSON เท่าที่มี) */
export const describeItem = (item) => {
  const r = parseRemark(item?.remark);
  const parts = [];
  if (Array.isArray(r.highlights) && r.highlights.length) parts.push(r.highlights.join(" · "));
  else if (r.plan) parts.push(`แพ็กเกจ ${String(r.plan).toUpperCase()}`);
  if (r.cycle) {
    const months = CYCLE_MONTHS[r.cycle];
    parts.push(`รอบบริการ${CYCLE_LABEL[r.cycle] ? " " + CYCLE_LABEL[r.cycle] : " " + r.cycle}${months ? ` (${months} เดือน)` : ""}`);
  }
  if (r.seats) parts.push(`จำนวน ${Number(r.seats).toLocaleString("en-US")} User`);
  if (r.kind === "service" || r.kind === "custom") {
    if (r.mandays) parts.push(`${r.mandays} manday x ${Number(r.ratePerManday || 0).toLocaleString("en-US")} บาท`);
  } else if (r.kind && r.key) parts.push(`Add-on ${r.kind === "module" ? "แพ็กเกจ" : "โควตา"}: ${r.key}`);
  if (r.service && r.quantity) parts.push(`จำนวน ${Number(r.quantity).toLocaleString("en-US")} ${r.unit || "คน"}`);
  else if (r.points) parts.push(`${Number(r.points).toLocaleString("en-US")} ${r.wallet === "ai" ? "เครดิต AI" : "point"}`);
  if (!r.cycle && r.days) parts.push(`ระยะเวลา ${r.days} วัน`);
  const start = fmtDate(pick(r, ["period_start", "service_start", "start_date", "starts_at", "start"]));
  const end = fmtDate(pick(r, ["period_end", "service_end", "end_date", "ends_at", "end"]));
  if (start || end) parts.push(`ช่วงบริการ ${start || "-"} - ${end || "-"}`);
  return parts.join(" · ");
};

// ที่อยู่ลูกค้า รูปแบบเดียวกับใบ zenith_market (Report.js tax_invoice_definition) ไม่มี = ไม่พิมพ์บรรทัด
const customerAddress = (item) =>
  `${item?.address || ""} ${item?.sub_district ? "ต." + item.sub_district : ""} ${item?.district ? "อ." + item.district : ""} ${item?.province ? "จ." + item.province : ""} ${item?.zipcode || ""}`
    .replace(/\s+/g, " ")
    .trim();

const sellerLines = (seller) => {
  const lines = [];
  const name = seller?.legal_name || seller?.company_name;
  if (name) lines.push({ text: name, bold: true, fontSize: 14 });
  if (seller?.legal_address) lines.push({ text: seller.legal_address, fontSize: 12 });
  if (seller?.tax_id) lines.push({ text: `เลขประจำตัวผู้เสียภาษี ${seller.tax_id}`, fontSize: 12 });
  if (seller?.phone) lines.push({ text: `โทร ${seller.phone}`, fontSize: 12 });
  return lines;
};

// ---- SaaS template ---------------------------------------------------------
export const buildSaasInvoiceDefinition = (item, { brand, logoDataUrl, color }) => {
  const summary = computeOrderSummary(item);
  const order_items = Array.isArray(item?.order_items) ? item.order_items : [];
  const goods_items = order_items.filter((e) => !isDiscountItem(e));
  const grand_total = summary.total !== null ? summary.total : summary.amount;
  const doc_no = `INV-${item?.order_no || ""}`;
  // วันที่ชำระเงินของ transaction (backend ยังไม่ส่งเวลาชำระ ใช้ transaction.created_at แทน) ไม่ใช้วันที่สร้าง order
  const tx = item?.transaction;
  const doc_date = fmtDate(tx?.payment_date || tx?.paid_at || tx?.created_at || item?.created_at);
  const ref = item?.transaction?.reference_id;
  const customer = item?.customer?.name_th || item?.customer?.name_en || "";
  const seller_block = sellerLines(item?.seller);
  const customer_address = customerAddress(item);
  const adjust = summary.goods - summary.discount - summary.subtotal;
  const row = (th, en, value) => [{ stack: [th, { text: en, fontSize: 11, color: "#6b7280" }] }, { text: value, alignment: "right" }];
  const added = summary.vat_mode === "added";

  // added: ราคาไม่รวม VAT -> รวมเงิน, ค่าดำเนินการ, ส่วนลด, ยอดก่อนภาษี, VAT 7%, รวมสุทธิ (ลงตัวเท่า total_price)
  // included: fallback เมื่อบวกไม่ลงตัว -> VAT รวมอยู่ในยอดชำระแล้ว แสดงอย่างเดียว
  const summary_rows = (added
    ? [
        row("รวมเงิน", "Subtotal", toFixed(summary.goods)),
        summary.fee !== 0 ? row("ค่าดำเนินการ", "Processing fee", toFixed(summary.fee)) : null,
        summary.discount > 0 ? row("ส่วนลด", "Discount", `-${toFixed(summary.discount)}`) : null,
        row("ยอดก่อนภาษี", "Amount before VAT", toFixed(summary.pre_tax)),
        row("ภาษีมูลค่าเพิ่ม 7%", "VAT", toFixed(summary.vat)),
      ]
    : [
        Math.abs(adjust) > 0.001 ? row("ปรับปรุงยอดเงิน", "Adjust amount", toFixed(adjust)) : null,
        row("รวมเงิน", "Subtotal", toFixed(summary.goods)),
        summary.discount > 0 ? row("ส่วนลด", "Discount", `-${toFixed(summary.discount)}`) : null,
        summary.fee !== 0 ? row("ค่าดำเนินการ", "Processing fee", toFixed(summary.fee)) : null,
        row("ภาษีมูลค่าเพิ่ม 7% (รวมในราคาแล้ว)", "VAT included", toFixed(summary.vat)),
      ]
  ).filter(Boolean);

  const label = (text) => ({ text, color: "#6b7280", fontSize: 12 });

  return {
    pageSize: "A4",
    pageMargins: [36, 36, 36, 36],
    content: [
      // ---- header: โลโก้ + แบรนด์ | ชื่อเอกสาร
      {
        columns: [
          {
            width: "*",
            columnGap: 10,
            columns: [
              logoDataUrl ? { image: logoDataUrl, width: 44, height: 44 } : { text: "", width: 0 },
              {
                width: "*",
                stack: [{ text: brand, fontSize: 24, bold: true, color }, ...seller_block],
              },
            ],
          },
          {
            width: 200,
            stack: [
              { text: "ใบกำกับภาษี / ใบเสร็จรับเงิน", alignment: "right", fontSize: 18, bold: true },
              { text: "TAX INVOICE / RECEIPT", alignment: "right", fontSize: 12, color },
              { text: doc_no, alignment: "right", bold: true, margin: [0, 6, 0, 0] },
              doc_date ? { text: `วันที่ ${doc_date}`, alignment: "right", fontSize: 12 } : { text: "" },
            ],
          },
        ],
      },
      { canvas: [{ type: "rect", x: 0, y: 0, w: 523, h: 3, color }], margin: [0, 10, 0, 10] },

      // ---- ลูกค้า + การชำระเงิน
      {
        columns: [
          {
            width: "*",
            stack: [
              label("ชื่อลูกค้า / ที่อยู่ในการจัดส่ง"),
              { text: customer || "-", bold: true },
              customer_address ? { text: customer_address, fontSize: 12 } : { text: "" },
              item?.customer?.phone ? { text: `โทร ${item.customer.phone}`, fontSize: 12 } : { text: "" },
            ],
          },
          {
            width: 240,
            stack: [
              label("หมายเลขอ้างอิง/Reference No."),
              // แสดงแค่ 10 ตัวท้ายของเลขอ้างอิงการชำระเงิน (ผู้ใช้กำหนด 2026-10-13)
              ref ? { text: `ref_${String(ref).slice(-10)}`, fontSize: 12 } : { text: "" },
            ],
          },
        ],
        margin: [0, 0, 0, 12],
      },

      // ---- รายการ
      {
        table: {
          headerRows: 1,
          widths: [24, "*", 40, 70, 80],
          body: [
            [
              { text: "#", style: "th" },
              { text: "รายการ / Description", style: "th", alignment: "left" },
              { text: "จำนวน", style: "th" },
              { text: "ราคา/หน่วย", style: "th", alignment: "right" },
              { text: "จำนวนเงิน", style: "th", alignment: "right" },
            ],
            ...goods_items.map((e, i) => {
              const detail_line = describeItem(e);
              return [
                { text: String(i + 1), alignment: "center" },
                {
                  stack: [
                    { text: e.product_name || "-", bold: true },
                    detail_line ? { text: detail_line, fontSize: 12, color: "#6b7280" } : { text: "" },
                  ],
                },
                { text: String(e.quantity ?? ""), alignment: "center" },
                { text: toFixed(e.price), alignment: "right" },
                { text: toFixed(itemLineTotal(e)), alignment: "right" },
              ];
            }),
          ],
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: (i) => (i === 0 || i === 1 ? color : "#e5e7eb"),
          paddingTop: () => 5,
          paddingBottom: () => 5,
          fillColor: (row) => (row === 0 ? color : null),
        },
      },
      { text: " ", margin: [0, 4] },

      // ---- สรุปยอด
      {
        columns: [
          {
            width: "*",
            stack: [
              { text: "จำนวนเงินตัวอักษร", color: "#6b7280", fontSize: 12 },
              { text: ThaiBahtText(grand_total), bold: true },
              { text: "ชำระผ่าน QR PromptPay", fontSize: 12, color: "#6b7280", margin: [0, 8, 0, 0] },
            ],
          },
          {
            width: 270,
            stack: [
              {
                table: { widths: [170, 60], body: summary_rows },
                layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 8, paddingRight: () => 8, paddingTop: () => 3, paddingBottom: () => 3 },
              },
              {
                table: {
                  widths: [110, 120],
                  body: [
                    [
                      { stack: [{ text: "รวมสุทธิ", bold: true, color: "#ffffff" }, { text: "Grand Total", fontSize: 11, color: "#ffffff" }] },
                      { text: toFixed(grand_total), bold: true, fontSize: 17, alignment: "right", noWrap: true, color: "#ffffff", margin: [0, 4, 0, 0] },
                    ],
                  ],
                },
                layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => color, paddingLeft: () => 8, paddingRight: () => 8, paddingTop: () => 4, paddingBottom: () => 4 },
                margin: [0, 4, 0, 0],
              },
            ],
          },
        ],
      },
      { text: "เอกสารฉบับนี้ออกโดยระบบอัตโนมัติ", alignment: "center", fontSize: 11, color: "#9ca3af", margin: [0, 40, 0, 0] },
    ],
    styles: { th: { bold: true, alignment: "center", color: "#ffffff" } },
    defaultStyle: { font: "THSarabunNew", fontSize: 15, lineHeight: 0.9 },
  };
};

const logoCache = new Map();
const cachedLogo = async (url) => {
  if (!logoCache.has(url)) logoCache.set(url, await imageToDataUrl(url));
  return logoCache.get(url);
};

/** docDefinition ของใบกำกับภาษี/ใบเสร็จของ Marketplace order เลือก template ตาม process_type ของ order */
export const build_marketplace_invoice = async (item) => {
  const process_type = getOrderProcessType(item);
  const { brand, logo, color, template } = resolveInvoiceBrand(process_type);
  if (template === "legacy") return tax_invoice_definition(item);
  return buildSaasInvoiceDefinition(item, { brand, logoDataUrl: await cachedLogo(logo), color });
};

const invoiceFileName = (item) => `tax-invoice-${item?.order_no || "order"}.pdf`;

/** ใบกำกับภาษี/ใบเสร็จรายใบ (ดาวน์โหลดทันที) */
export const report_marketplace_invoice = async (item) => {
  pdfMake.createPdf(await build_marketplace_invoice(item)).download(invoiceFileName(item));
};

// รวมหลายใบเป็น PDF เดียว ใบละหน้า: แต่ละใบห่อใน stack ที่พก font/fontSize/lineHeight (defaultStyle เดิมของใบนั้น)
// และ margin เสริมให้ขอบกระดาษเท่าของ template ตัวเอง (หน้ารวมใช้ขอบ 25/20 แบบใบเดิม)
const BULK_MARGINS = [25, 20, 25, 20];
export const buildBulkInvoiceDefinition = (definitions) => {
  const styles = {};
  const content = definitions.map((def, index) => {
    Object.assign(styles, def.styles || {});
    const [ml, mt, mr, mb] = def.pageMargins || BULK_MARGINS;
    return {
      stack: def.content,
      ...(def.defaultStyle || {}),
      margin: [ml - BULK_MARGINS[0], mt - BULK_MARGINS[1], mr - BULK_MARGINS[2], mb - BULK_MARGINS[3]],
      ...(index > 0 ? { pageBreak: "before" } : {}),
    };
  });
  return { pageSize: "A4", pageMargins: BULK_MARGINS, content, styles, defaultStyle: { font: "THSarabunNew", fontSize: 16, lineHeight: 0.9 } };
};

/** order หลายใบ -> PDF เดียว (ใบละหน้า) */
export const report_marketplace_invoices = async (items, file_name) => {
  const definitions = [];
  for (const item of items) definitions.push(await build_marketplace_invoice(item));
  pdfMake.createPdf(buildBulkInvoiceDefinition(definitions)).download(file_name);
};
